import React from 'react';

import { useIntlDictionary } from 'hooks/useIntlDictionary';
import {
  TableTr,
  TableWrapper,
  TableHead,
  TableTh,
  TableBody,
} from 'design/table';
import { Bill, BillsTableSortableField } from 'types/state/bill';
import { BillsTableRow } from 'containers/BillsTable/BillsTableRow';

type Props = {
  bills: Bill[];
  sortedBy: BillsTableSortableField;
  ascendingSort: boolean;
  activeItemId?: number;
  onSortChange(nextSortBy: BillsTableSortableField): void;
  onItemClick(item: number): void;
};

export const BillsTable = (props: Props) => {
  const {
    bills,
    sortedBy,
    ascendingSort,
    activeItemId,
    onSortChange,
    onItemClick,
  } = props;

  const { billsTableColumns: columnsIntl } = useIntlDictionary();

  const onIdClick = React.useCallback(() => {
    onSortChange('id');
  }, [onSortChange]);

  const onOrganizationClick = React.useCallback(() => {
    onSortChange('organization');
  }, [onSortChange]);

  const onFullNameClick = React.useCallback(() => {
    onSortChange('fullName');
  }, [onSortChange]);

  const onResidentClick = React.useCallback(() => {
    onSortChange('resident');
  }, [onSortChange]);

  const onAmountClick = React.useCallback(() => {
    onSortChange('amount');
  }, [onSortChange]);

  const onAmountEURClick = React.useCallback(() => {
    onSortChange('amountEUR');
  }, [onSortChange]);

  const onAmountRUBClick = React.useCallback(() => {
    onSortChange('amountRUB');
  }, [onSortChange]);

  const onStatusClick = React.useCallback(() => {
    onSortChange('status');
  }, [onSortChange]);

  const onIssuedAtClick = React.useCallback(() => {
    onSortChange('issuedAt');
  }, [onSortChange]);

  const tableRowsNode = React.useMemo(() => {
    return bills.map((bill) => (
      <BillsTableRow
        key={bill.id}
        active={activeItemId === bill.id}
        bill={bill}
        onClick={onItemClick}
      />
    ));
  }, [bills, activeItemId, onItemClick]);

  return (
    <TableWrapper>
      <TableHead>
        <TableTr>
          <TableTh
            size="xs"
            sorted={sortedBy === 'id'}
            ascending={ascendingSort}
            text={columnsIntl.id}
            onClick={onIdClick}
          />
          <TableTh size="m" ascending={ascendingSort} text={columnsIntl.unp} />
          <TableTh
            size="auto"
            sorted={sortedBy === 'organization'}
            ascending={ascendingSort}
            text={columnsIntl.organization}
            onClick={onOrganizationClick}
          />
          <TableTh
            size="xxl"
            sorted={sortedBy === 'fullName'}
            ascending={ascendingSort}
            text={columnsIntl.fullName}
            onClick={onFullNameClick}
          />
          <TableTh
            size="m"
            sorted={sortedBy === 'resident'}
            ascending={ascendingSort}
            text={columnsIntl.resident}
            onClick={onResidentClick}
          />
          <TableTh
            size="m"
            sorted={sortedBy === 'amount'}
            ascending={ascendingSort}
            text={columnsIntl.amount}
            onClick={onAmountClick}
          />
          <TableTh
            size="m"
            sorted={sortedBy === 'amountEUR'}
            ascending={ascendingSort}
            text={columnsIntl.amountEUR}
            onClick={onAmountEURClick}
          />
          <TableTh
            size="m"
            sorted={sortedBy === 'amountRUB'}
            ascending={ascendingSort}
            text={columnsIntl.amountRUB}
            onClick={onAmountRUBClick}
          />
          <TableTh
            size="m"
            sorted={sortedBy === 'status'}
            ascending={ascendingSort}
            text={columnsIntl.status}
            onClick={onStatusClick}
          />
          <TableTh
            size="m"
            sorted={sortedBy === 'issuedAt'}
            ascending={ascendingSort}
            text={columnsIntl.issuedAt}
            onClick={onIssuedAtClick}
          />
        </TableTr>
      </TableHead>
      <TableBody>{tableRowsNode}</TableBody>
    </TableWrapper>
  );
};
